import { useState, FormEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Contact modal component.
 *
 * Fixed issues:
 * 1. The `<i>` element was written as an opening‑and‑closing tag (`<i></i>`).  
 *    When JSX is compiled with certain configurations this can be parsed as an
 *    unexpected "/" token, producing the *"Expected identifier but found '/'"*
 *    error.  The element is now self‑closed (`<i … />`) which eliminates the
 *    parsing problem.
 * 2. Added defensive error handling for network failures and timeout.
 * 3. Wrapped the fetch call in a timeout to avoid hanging requests.
 */
export default function ContactModal({ isOpen, onClose }: ContactModalProps) {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    email: '',
    telefono: '',
    pais: '',
    mensaje: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>(
    'idle'
  );

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  /**
   * Helper to add a timeout to fetch.
   */
  const fetchWithTimeout = (
    resource: string,
    options: {
      method?: string;
      headers?: Record<string, string>;
      body?: string;
      signal?: AbortSignal;
    } = {},
    timeout = 10000
  ): Promise<Response> => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    const signal = controller.signal;

    return fetch(resource, { ...options, signal })
      .finally(() => clearTimeout(id));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetchWithTimeout(
        'https://readdy.ai/api/form/d5ufu36s8bb84h5iee3g',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody.toString(),
        },
        15000 // 15 seconds timeout
      );

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      // Success path
      setSubmitStatus('success');
      setFormData({
        nombre: '',
        empresa: '',
        email: '',
        telefono: '',
        pais: '',
        mensaje: '',
      });

      // Auto‑close after a short delay
      setTimeout(() => {
        onClose();
        setSubmitStatus('idle');
      }, 2000);
    } catch (error) {
      // Distinguish abort (timeout) from other errors
      if ((error as any).name === 'AbortError') {
        console.error('Form submission timed out');
      } else {
        console.error('Error submitting form:', error);
      }
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[88svh] sm:max-h-[90vh] overflow-y-auto sm:mx-4">
        <div className="sticky top-0 bg-white z-10 px-4 sm:px-8 pt-4 sm:pt-8 pb-2.5 sm:pb-4 border-b border-gray-100 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <h3 className="text-lg sm:text-3xl font-bold text-[#1A1A1A]">
              {t('modal.title')}
            </h3>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer rounded-full hover:bg-gray-100 transition-colors"
            >
              {/* Self‑closed <i> element fixes the parsing error */}
              <i className="ri-close-line text-2xl" />
            </button>
          </div>
        </div>

        <div className="px-4 sm:px-8 pb-4 sm:pb-8 pt-3 sm:pt-4">
          <form id="contact-form" onSubmit={handleSubmit} data-readdy-form="">
            <div className="space-y-2.5 sm:space-y-4">
              {/* Nombre */}
              <div>
                <label
                  htmlFor="nombre"
                  className="block text-xs sm:text-sm font-medium text-[#1A1A1A] mb-1"
                >
                  {t('modal.nombre')}
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  required
                  value={formData.nombre}
                  onChange={(e) =>
                    setFormData({ ...formData, nombre: e.target.value })
                  }
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B8E23] text-sm"
                />
              </div>

              {/* Empresa */}
              <div>
                <label
                  htmlFor="empresa"
                  className="block text-xs sm:text-sm font-medium text-[#1A1A1A] mb-1"
                >
                  {t('modal.empresa')}
                </label>
                <input
                  type="text"
                  id="empresa"
                  name="empresa"
                  required
                  value={formData.empresa}
                  onChange={(e) =>
                    setFormData({ ...formData, empresa: e.target.value })
                  }
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B8E23] text-sm"
                />
              </div>

              {/* Email & Teléfono */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-xs sm:text-sm font-medium text-[#1A1A1A] mb-1"
                  >
                    {t('modal.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B8E23] text-sm"
                  />
                </div>

                <div>
                  <label
                    htmlFor="telefono"
                    className="block text-xs sm:text-sm font-medium text-[#1A1A1A] mb-1"
                  >
                    {t('modal.telefono')}
                  </label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={(e) =>
                      setFormData({ ...formData, telefono: e.target.value })
                    }
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B8E23] text-sm"
                  />
                </div>
              </div>

              {/* País */}
              <div>
                <label
                  htmlFor="pais"
                  className="block text-xs sm:text-sm font-medium text-[#1A1A1A] mb-1"
                >
                  {t('modal.pais')}
                </label>
                <input
                  type="text"
                  id="pais"
                  name="pais"
                  required
                  value={formData.pais}
                  onChange={(e) =>
                    setFormData({ ...formData, pais: e.target.value })
                  }
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B8E23] text-sm"
                />
              </div>

              {/* Mensaje */}
              <div>
                <label
                  htmlFor="mensaje"
                  className="block text-xs sm:text-sm font-medium text-[#1A1A1A] mb-1"
                >
                  {t('modal.mensaje')}
                </label>
                <textarea
                  id="mensaje"
                  name="mensaje"
                  required
                  rows={3}
                  maxLength={500}
                  value={formData.mensaje}
                  onChange={(e) =>
                    setFormData({ ...formData, mensaje: e.target.value })
                  }
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B8E23] resize-none text-sm"
                  placeholder={t('modal.placeholder')}
                />
                <p className="text-[10px] sm:text-[11px] text-gray-500 mt-0.5">
                  {formData.mensaje.length}/500 {t('modal.chars')}
                </p>
              </div>
            </div>

            {/* Status messages */}
            {submitStatus === 'success' && (
              <div className="mt-2.5 p-2.5 sm:p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-xs sm:text-sm">
                  {t('modal.success')}
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-2.5 p-2.5 sm:p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-xs sm:text-sm">
                  {t('modal.error')}
                </p>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-3 sm:mt-6 bg-[#d6df00] text-[#1A1A1A] px-6 py-2.5 sm:py-4 rounded-full text-[0.95rem] sm:text-lg font-medium hover:bg-[#c2cb00] transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? t('modal.sending') : t('modal.send')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
